import type { ApiError } from '@approvalmax/types';
import { openSplitViewState } from '@approvalmax/ui/src/components';
import { miscHelpers, routerHelpers } from '@approvalmax/utils';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { State } from 'modules/data';
import { mergeDeep } from 'modules/immutable';
import { hooks as pageHooks } from 'modules/page';
import { useSelector } from 'modules/react-redux';
import { stringify } from 'query-string';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { notificationService } from 'services/notification';
import { ContentSplitViewMode, contentSplitViewState } from 'shared/states';

import { loadOneRequest, loadPageData, loadRequests, setActiveRequestId } from '../actions';
import { COMPANY_REQUESTS_LIST_PAGE_ID, CompanyRequestListFilter, QueryKeys } from '../config';
import { checkRequestLoaded } from '../selectors/pageSelectors';
import { CompanyRequestsListPageQuery } from './useCompanyRequestsListPageData.types';

const useCompanyRequestsListPageData = () => {
    const dispatch = useDispatch();

    const setOpenSplitView = useSetAtom(openSplitViewState);
    const setContentSplitView = useSetAtom(contentSplitViewState);

    const { companyId, section } = useParams<{ companyId: string; section: string }>();

    const company = useSelector((state: State) => selectors.company.findCompanyById(state, companyId));

    const location = useLocation();
    const history = useHistory();

    const activeRequestId = routerHelpers.getParamAsString(location.search, 'requestId', '');
    const attachmentId = routerHelpers.getParamAsString(location.search, 'attachmentId', '');
    const auditReport = routerHelpers.getParamAsString(location.search, 'auditReport', '');
    const showPaymentSummary = routerHelpers.getParamAsString(location.search, 'showPaymentSummary', '');

    const firstRender = useRef(true);

    const isRequestLoaded = useSelector((state: State) => checkRequestLoaded(state, activeRequestId));

    const filter = miscHelpers.isEnumValue(CompanyRequestListFilter, section)
        ? section
        : CompanyRequestListFilter.CompanyAll;

    const noCompanies = pageHooks.useNoCompanies();

    const { isInitialLoading, isError, error } = useQuery<CompanyRequestsListPageQuery, ApiError>(
        [QueryKeys.CompanyRequestsListPage, companyId, filter],
        async () => {
            if (!company) {
                if (activeRequestId) {
                    notificationService.showErrorToast('Sorry, you have no access to this request.');
                }

                // TODO: remove this if backend starts to throw error for wrong companyId
                throw Error('Company not found');
            }

            const requestsResponse = await loadRequests({ companyId, templateId: null, filter });

            const oneRequestResponse = await loadOneRequest(
                firstRender.current ? activeRequestId || '' : '',
                companyId
            );

            if (firstRender.current && oneRequestResponse) {
                const { entities, request } = oneRequestResponse;

                if (request.companyId !== companyId) throw Error('Request not found');

                if (!requestsResponse.requests.includes(request.id)) {
                    requestsResponse.requests = [request.id, ...requestsResponse.requests];
                    requestsResponse.entities = mergeDeep(requestsResponse.entities, entities);
                }
            }

            const firstRequestId = requestsResponse.requests[0];
            const requestId = activeRequestId || firstRequestId;

            firstRender.current = false;

            return {
                ...requestsResponse,
                requestId,
                attachmentId,
                auditReport,
            };
        },
        {
            onSuccess: (data) => {
                dispatch(
                    loadPageData({
                        filter,
                        companyId,
                        templateId: null,
                        requestId: data.requestId,
                        continuationToken: data.continuationToken,
                        requests: data.requests,
                        hasMore: data.hasMore,
                        entities: data.entities,
                        innerPageId: COMPANY_REQUESTS_LIST_PAGE_ID,
                    })
                );

                if (data.attachmentId) {
                    setOpenSplitView(true);
                    setContentSplitView({
                        attachmentId: attachmentId,
                        mode: ContentSplitViewMode.AttachmentsToUnknown,
                    });
                }

                if (data.auditReport) {
                    setOpenSplitView(true);
                    setContentSplitView({
                        mode: ContentSplitViewMode.AuditReport,
                    });
                }

                navigateToRequest(data.requestId, Boolean(data.auditReport), showPaymentSummary === 'true');
            },
            enabled: !noCompanies,
        }
    );

    const navigateToRequest = (requestId: string, auditReport: boolean, showPaymentSummary: boolean) => {
        history.push({
            pathname: location.pathname,
            search: stringify({
                requestId: requestId,
                auditReport: auditReport ? 'true' : undefined,
                showPaymentSummary: showPaymentSummary ? 'true' : undefined,
            }),
        });
    };

    useEffect(() => {
        if (firstRender.current) return;

        if (isRequestLoaded) {
            dispatch(setActiveRequestId(activeRequestId));
        }
    }, [activeRequestId, dispatch, isRequestLoaded]);

    return {
        isInitialLoading,
        isError,
        error,
    };
};

export default useCompanyRequestsListPageData;
